import AlertMinimalIcon from '@assets/icons/alert-minimal.svg';
import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useRef } from 'react';
import { addToOrderPrebookItemDisabled } from '@/contexts/item-context-utils';
import { ApplicationType } from '@/contexts/dynamic-config';
import { Button } from '@/components/button';
import { ChatStatus } from '@/enums';
import { createStyles } from '@/contexts/theme-context';
import { EssentialItemButton } from './essential-item-button';
import { getAddedVariantPackEventPayloads } from '@/utils/ads';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import { ItemSubtitleV2 } from '@/components/item-subtitle-v2';
import { ListItemDisplayText } from './list-item-display-text';
import {
  ORDER_ITEM_QUANTITY_HEIGHT,
  QUANTITY_INPUT_WIDTH,
  QuantitySelector,
  TEXT_INPUT_CONTAINER_SPACING,
  ZeroQuantityBehavior,
} from '@/components/quantity-selector';
import {
  Platform,
  StyleProp,
  StyleSheet,
  Text,
  View,
  ViewStyle,
} from 'react-native';
import { SearchNote } from '@/components/order-guide/search-note';
import { TYPOGRAPHY } from '@/styles';
import { UpdateQuantityFn } from '@/contexts/new-order-actions-context-types';
import {
  useAddToOrderDisabled,
  useAvailabilityStatusEnabled,
} from '@/hooks/use-add-to-order-disabled';
import { useCanWriteLineItemRequest } from '@/hooks/dynamic-config/use-can-write-line-item-request';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useHover } from 'react-native-web-hooks';
import { useItemLabels } from '@/hooks/use-item-labels';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useOrderGuideItemsContext } from '@/contexts/order-guide-items-context';
import { usePriceEditing } from '@/hooks/use-price-editing';
import { useSearchNoteActionsContext } from '@/contexts/search-note-actions-context';
import { useUserContext } from '@/contexts/user-context';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

export type RenderActionsFn = (
  metadata: ItemListOptionMetadata,
) => React.ReactNode;

interface Props {
  canDecrementToZero?: boolean;
  disabled?: boolean;
  disableTextInput?: boolean;
  heading?: React.ReactNode;
  inDraggableSheet?: boolean;
  isCompactView?: boolean;
  isFocused: boolean;
  item: ItemListOptionMetadata;
  onNavigateToBecomeCustomer?: () => void;
  quantity?: number;
  quantityInStock?: number;
  renderActions?: RenderActionsFn;
  renderOrderGuideButton?: (variantPackUUID: string) => React.ReactNode;
  showInlineModifiers?: boolean;
  showListView?: boolean;
  showTooltipOnMount?: boolean;
  updateQuantity?: UpdateQuantityFn;
  wrapperStyle?: StyleProp<ViewStyle>;
  zeroQuantityBehavior?: ZeroQuantityBehavior;
}

const WRAPPER_LIST_WIDTH =
  2 * ORDER_ITEM_QUANTITY_HEIGHT +
  QUANTITY_INPUT_WIDTH +
  2 * TEXT_INPUT_CONTAINER_SPACING;

export const ItemDetailActions: React.FC<Props> = ({
  canDecrementToZero,
  disabled,
  disableTextInput,
  heading,
  inDraggableSheet = false,
  isCompactView = false,
  isFocused,
  item,
  onNavigateToBecomeCustomer,
  quantity,
  quantityInStock,
  renderActions,
  renderOrderGuideButton,
  showListView = false,
  showTooltipOnMount,
  updateQuantity,
  wrapperStyle,
  zeroQuantityBehavior,
}) => {
  const [noteStyles] = useNoteStyles();
  const noteContainerRef = useRef<View>(null);
  const editButtonRef = useRef<View>(null);
  const isPaneHovered = useHover(noteContainerRef);
  const isEditHovered = useHover(editButtonRef);
  const showEditOverlay =
    Platform.OS === 'web' && (isPaneHovered || isEditHovered);

  const orderGuideButton = renderOrderGuideButton?.(item.variantPackUUID);
  const availabilityStatusEnabled = useAvailabilityStatusEnabled();
  const { essentialVariantPackUUIDs } = useVariantPackInfoContext();
  const isAddToOrderDisabled = useAddToOrderDisabled(item.variantPackUUID);
  const { customOrderGuideEditingEnabled, supplierTimezone } =
    useNewOrderContext();
  const {
    selectedRestaurantRelationship: { chatStatus },
  } = useUserContext();
  const searchNoteActions = useSearchNoteActionsContext();
  const { variantPackPartitionMap, partitionToCollectionMap } =
    useOrderGuideItemsContext();
  const isCatalogLimited =
    useDynamicConfigValue('application_type') === ApplicationType.CATALOG_ONLY;
  const canWriteRequest = useCanWriteLineItemRequest();

  const { isSalesRepPeakPriceProtectionInEffect } = usePriceEditing(
    item.variantPackUUID,
  );

  const isAddToOrderPrebookItemDisabled = useMemo(
    () => addToOrderPrebookItemDisabled(item, supplierTimezone),
    [item, supplierTimezone],
  );

  const itemLabels = useItemLabels(item, isSalesRepPeakPriceProtectionInEffect);

  const isProspect = chatStatus === ChatStatus.PROSPECT;
  const isEssential = essentialVariantPackUUIDs.has(item.variantPackUUID);
  const isItemUnavailable =
    isAddToOrderDisabled || isAddToOrderPrebookItemDisabled;

  const showSearchActions =
    searchNoteActions && !inDraggableSheet && customOrderGuideEditingEnabled;

  const {
    isItemInOrderGuide,
    memoCollectionCount,
    memoPartitionCount,
    hasRequest,
    noteText,
  } = useMemo(() => {
    if (!showSearchActions) {
      return {
        hasRequest: false,
        isItemInOrderGuide: false,
        memoCollectionCount: 0,
        memoPartitionCount: 0,
        noteText: undefined,
      };
    }

    const partitions = variantPackPartitionMap[item.variantPackUUID];
    if (!partitions) {
      return {
        hasRequest: false,
        isItemInOrderGuide: false,
        memoCollectionCount: 0,
        memoPartitionCount: 0,
        noteText: undefined,
      };
    }

    let foundRequest = false;
    const collectionsWithMemos = new Set<string>();
    const partitionsWithMemos = new Set<string>();
    let noteText = undefined;

    Object.entries(partitions).forEach(([partitionUUID, orderGuideItem]) => {
      const collectionUUID = partitionToCollectionMap.get(partitionUUID);
      if (!collectionUUID) {
        return;
      }

      const { memo, request } = orderGuideItem.order_guide_restaurant || {};
      if (memo) {
        collectionsWithMemos.add(collectionUUID);
        partitionsWithMemos.add(partitionUUID);
        noteText = memo;
      }
      if (request) {
        foundRequest = true;
      }
    });

    return {
      hasRequest: foundRequest,
      isItemInOrderGuide: true,
      memoCollectionCount: collectionsWithMemos.size,
      memoPartitionCount: partitionsWithMemos.size,
      noteText,
    };
  }, [
    showSearchActions,
    item.variantPackUUID,
    variantPackPartitionMap,
    partitionToCollectionMap,
  ]);

  const renderQuantitySelector = useCallback(() => {
    return (
      <View style={styles.quantityContainer}>
        <QuantitySelector
          additionalIcon={isEssential ? AlertMinimalIcon : null}
          canDecrementToZero={canDecrementToZero}
          disabled={disabled}
          disableTextInput={disableTextInput}
          inDraggableSheet={inDraggableSheet}
          isFocused={isFocused}
          itemDisplayName={item.displayName}
          maxQuantity={item.maximumQuantity}
          minQuantity={item.minimumQuantity}
          onChange={(previousQuantity: number, newQuantity: number) => {
            updateQuantity?.({
              eventPayloads: getAddedVariantPackEventPayloads(item),
              newQuantity,
              previousQuantity,
              variantPackDetails: {
                itemDisplayName: item.displayName,
                metadata: item.variantPackMetadata,
              },
              variantPackUUID: item.variantPackUUID,
            });
          }}
          onNavigateToBecomeCustomer={onNavigateToBecomeCustomer}
          quantity={quantity ?? 0}
          showTooltipOnMount={showTooltipOnMount}
          zeroQuantityBehavior={zeroQuantityBehavior}
        />
      </View>
    );
  }, [
    isEssential,
    canDecrementToZero,
    disabled,
    disableTextInput,
    inDraggableSheet,
    isFocused,
    item,
    onNavigateToBecomeCustomer,
    quantity,
    showTooltipOnMount,
    zeroQuantityBehavior,
    updateQuantity,
  ]);

  const hasNotes = memoCollectionCount > 0 || (hasRequest && canWriteRequest);

  const unifiedSearchActions = useMemo(() => {
    if (!searchNoteActions) {
      return null; // should never happen, typescript check
    }

    return (
      <View style={styles.searchActionsContainer}>
        {isItemInOrderGuide ? (
          <View ref={noteContainerRef} style={styles.searchNoteWrapper}>
            {hasNotes ? (
              <>
                <View
                  pointerEvents={showEditOverlay ? 'none' : 'auto'}
                  style={showEditOverlay ? styles.notePaneHidden : undefined}
                >
                  <SearchNote
                    memoCollectionCount={memoCollectionCount}
                    memoPartitionCount={memoPartitionCount}
                    noteText={noteText}
                    onPress={() =>
                      searchNoteActions.onNotePress({
                        ...item,
                        skus: [item],
                      })
                    }
                    showRequest={hasRequest && canWriteRequest}
                  />
                </View>
                <View
                  pointerEvents={showEditOverlay ? 'auto' : 'none'}
                  style={[
                    styles.notePaneOverlay,
                    !showEditOverlay && styles.notePaneHidden,
                  ]}
                >
                  <Button
                    contentStyle={styles.noteEditButtonContent}
                    focusable={false}
                    hoverStyle={noteStyles.noteActionTextHover}
                    onPress={() =>
                      searchNoteActions.onNotePress({
                        ...item,
                        skus: [item],
                      })
                    }
                    ref={editButtonRef}
                    size="small"
                    textProps={{ numberOfLines: 1 }}
                    textStyle={noteStyles.noteActionText}
                    trackingData={{
                      name: 'order_guide_list_item.edit_note_button',
                      properties: { variantPackUUID: item.variantPackUUID },
                    }}
                    type="tertiary"
                  >
                    {i18n.t('edit_note')}
                  </Button>
                </View>
              </>
            ) : (
              <Button
                focusable={false}
                hoverStyle={noteStyles.noteActionTextHover}
                onPress={() =>
                  searchNoteActions.onNotePress({
                    ...item,
                    skus: [item],
                  })
                }
                size="small"
                textProps={{ numberOfLines: 1 }}
                textStyle={noteStyles.noteActionText}
                trackingData={{
                  name: 'search_desktop.add_note_button',
                  properties: { variantPackUUID: item.variantPackUUID },
                }}
                type="tertiary"
              >
                {i18n.t('add_note')}
              </Button>
            )}
          </View>
        ) : null}
        {isItemInOrderGuide && !isCatalogLimited ? (
          <EssentialItemButton
            itemMetadata={item}
            variantPackUUID={item.variantPackUUID}
          />
        ) : null}
        {isCatalogLimited ? null : isItemUnavailable ? (
          availabilityStatusEnabled ? null : (
            <View style={styles.quantityContainer}>
              <Text style={TYPOGRAPHY.pSemiBold}>{i18n.t('unavailable')}</Text>
            </View>
          )
        ) : !isProspect && !isCompactView ? (
          renderQuantitySelector()
        ) : null}
      </View>
    );
  }, [
    searchNoteActions,
    isItemInOrderGuide,
    hasNotes,
    showEditOverlay,
    memoCollectionCount,
    memoPartitionCount,
    noteText,
    hasRequest,
    canWriteRequest,
    noteStyles.noteActionTextHover,
    noteStyles.noteActionText,
    item,
    isCatalogLimited,
    isItemUnavailable,
    availabilityStatusEnabled,
    isProspect,
    isCompactView,
    renderQuantitySelector,
  ]);

  const resolvedActions = useMemo(() => {
    if (showSearchActions) {
      return unifiedSearchActions;
    }

    if (isItemUnavailable) {
      if (availabilityStatusEnabled) {
        return null;
      }

      return (
        <View
          style={[
            sharedStyles.unavailable,
            styles.quantityWrapper,
            showListView ? sharedStyles.quantityWrapperListView : null,
          ]}
        >
          <Text style={TYPOGRAPHY.pSemiBold}>{i18n.t('unavailable')}</Text>
        </View>
      );
    }

    if (renderActions) {
      return renderActions(item);
    }

    if (!isProspect && !isCompactView) {
      return renderQuantitySelector();
    }

    return null;
  }, [
    showSearchActions,
    isItemUnavailable,
    renderActions,
    isProspect,
    isCompactView,
    unifiedSearchActions,
    availabilityStatusEnabled,
    showListView,
    item,
    renderQuantitySelector,
  ]);

  return (
    <>
      <View style={[styles.base, wrapperStyle]}>
        <View
          style={[
            styles.itemSubtitleContainer,
            showSearchActions
              ? styles.itemSubtitleNoShrink
              : styles.itemSubtitleMinWidth,
          ]}
        >
          {orderGuideButton}
          <ItemSubtitleV2
            baseUnit={item.baseUnit}
            currencyCode={item.currencyCode}
            displayString={item.packDisplayString}
            heading={heading}
            isCompactView={isCompactView}
            labels={itemLabels}
            modifierString={item.modifierString}
            promotionPriceInMicros={item.promotionPricePerUnitInMicros}
            quantityInStock={quantityInStock}
            sku={item.externalID}
            style={[
              (isAddToOrderDisabled || isAddToOrderPrebookItemDisabled) &&
              !availabilityStatusEnabled
                ? sharedStyles.outOfStockSubtitle
                : null,
              showListView && !orderGuideButton ? sharedStyles.subtitle : null,
            ]}
            unitCount={item.unitCount}
            unitPriceInMicros={item.unitPriceInMicros}
          />
        </View>
        {resolvedActions}
      </View>
      <ListItemDisplayText
        containerStyle={styles.listItemDisplayText}
        variantPackUUID={item.variantPackUUID}
      />
    </>
  );
};

const OUT_OF_STOCK_OPACITY = 0.6;
const BASE_HORIZONTAL_PADDING = 12;

export const sharedStyles = StyleSheet.create({
  outOfStockSubtitle: {
    opacity: OUT_OF_STOCK_OPACITY,
  },
  quantityWrapperListView: {
    alignItems: 'flex-end',
    width: WRAPPER_LIST_WIDTH,
  },
  subtitle: {
    alignSelf: 'flex-start',
  },
  unavailable: {
    height: ORDER_ITEM_QUANTITY_HEIGHT,
    justifyContent: 'center',
    opacity: OUT_OF_STOCK_OPACITY,
  },
});

const styles = StyleSheet.create({
  base: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: BASE_HORIZONTAL_PADDING,
    paddingVertical: 8,
  },
  itemSubtitleContainer: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    flexShrink: 1,
    gap: 8,
    marginRight: 6,
  },
  itemSubtitleMinWidth: {
    minWidth: '40%',
  },
  itemSubtitleNoShrink: {
    flexBasis: 'auto',
    flexShrink: 0,
  },
  listItemDisplayText: {
    paddingHorizontal: BASE_HORIZONTAL_PADDING,
  },
  noteEditButtonContent: {
    alignSelf: 'flex-end',
  },
  notePaneHidden: {
    opacity: 0,
  },
  notePaneOverlay: {
    alignItems: 'flex-end',
    bottom: 0,
    end: 0,
    justifyContent: 'center',
    position: 'absolute',
    start: 0,
    top: 0,
    zIndex: 1,
  },
  quantityContainer: {
    alignItems: 'flex-end',
    width: 138,
  },
  quantityWrapper: {
    alignItems: 'flex-end',
    justifyContent: 'center',
    marginTop: 4,
    maxWidth: '60%',
  },
  searchActionsContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    flexShrink: 1,
    gap: 16,
    overflow: 'visible',
    zIndex: 1,
  },
  searchNoteWrapper: {
    alignItems: 'flex-end',
    flexShrink: 1,
    overflow: 'visible',
    position: 'relative',
  },
});

const useNoteStyles = createStyles((theme) => ({
  noteActionText: {
    borderRadius: theme.sk.radii.radiusMedium,
    color: theme.colors.inactiveText,
    fontSize: theme.fontSizes.small,
    padding: theme.sk.spacing.spaceSmall,
  },
  noteActionTextHover: {
    backgroundColor: theme.sk.color.bg.button.tertiary.hover,
  },
}));
